import React, { useEffect, useRef, useState, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { isInGamePage, markUserInteraction } from '../utils/soundUtils';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

// Exportar las variantes de animación para que puedan ser utilizadas por otros componentes
export const contentVariants = {
  initial: { height: 0, opacity: 0 },
  animate: {
    height: 'auto',
    opacity: 1,
    transition: {
      height: { duration: 0.3, ease: 'easeInOut' },
      opacity: { duration: 0.2, delay: 0.1 },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.3, ease: 'easeInOut' },
      opacity: { duration: 0.2 },
    },
  },
};

export const messageVariants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
  exit: { opacity: 0, y: 10, transition: { duration: 0.15, ease: 'easeIn' } },
};

const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  className = '',
  maxWidth = 'lg',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalYOffset, setModalYOffset] = useState(0);
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // Clases responsivas para el ancho máximo del modal
  const maxWidthClasses: { [key in BaseModalProps['maxWidth']]: string } = {
    sm: 'max-w-sm sm:max-w-lg lg:max-w-2xl',
    md: 'max-w-md sm:max-w-xl lg:max-w-3xl',
    lg: 'max-w-2xl sm:max-w-3xl lg:max-w-4xl',
    xl: 'max-w-4xl sm:max-w-5xl lg:max-w-6xl',
    full: 'max-w-full',
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Manejo del teclado en móviles (ajusta la posición del modal)
    const handleResize = () => {
      if (!isMobile) return;

      const visualViewportHeight = window.visualViewport?.height;
      const windowHeight = window.innerHeight;
      if (
        visualViewportHeight &&
        windowHeight &&
        Math.abs(visualViewportHeight - windowHeight) > 100
      ) {
        setModalYOffset(-200);
      } else {
        setModalYOffset(0);
      }
    };

    if (isOpen) {
      markUserInteraction();
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('resize', handleResize);
      handleResize();
      document.body.classList.add('modal-open');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
      document.body.classList.remove('modal-open');
    };
  }, [isOpen, onClose, isMobile]);

  // Variantes de animación: en móviles solo se usa opacidad, mientras que en escritorio se agrega escalado
  const modalVariants = isMobile
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.8 },
      };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={(e) => e.stopPropagation()}
        >
          <motion.div
            ref={modalRef}
            variants={modalVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
            className={`bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full mx-4 ${maxWidthClasses[maxWidth]} max-h-screen overflow-hidden ${className}`}
            style={{ transform: `translateY(${modalYOffset}px)` }}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default BaseModal;
